import { useQuery } from '@apollo/client';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import useInfiniteScroll from 'react-infinite-scroll-hook';
import { useLocation } from 'react-router-dom';
import { useRecoilState, useRecoilValue } from 'recoil';

import greaseTrapIcon from '../../assets/icons/grease-trap.svg';
import addTransactionIcon from '../../assets/icons/transactions.svg';
import AddButton from '../../components/ui/common/AddButton';
import Breadcrumb from '../../components/ui/common/Breadcrumb';
import FilterMark from '../../components/ui/common/FilterMark';
import RangeDateField from '../../components/ui/common/Inputs/RangeDateField';
import Searchbar from '../../components/ui/common/Searchbar';
import SentryInfiniteScroll from '../../components/ui/common/SentryInfiniteScroll';
import LoaderSmall from '../../components/ui/LoaderSmall';
import { GreaseTrapCard } from '../../components/ui/vendors/grease-traps/GreaseTrapCard';
import StatCard from '../../components/ui/vendors/StatCard';
import useDebouceTime from '../../hooks/useDebounceTime';
import { useGreaseTrapList } from '../../hooks/useGreaseTrapList';
import { useModal } from '../../hooks/useModal';
import { greaseTrapCleanFiltersState } from '../../recoil/greaseTrapCleanFilter';
import { greaseTrapFiltersState } from '../../recoil/greaseTrapFilters';
import { needRefreshState } from '../../recoil/needRefresh';
import {
  GET_GREASE_TRAP_GLOBAL_STATS,
  GET_GREASE_TRAP_OPTIONS,
} from '../../services/graphql/vendors/grease-traps/greaseTrapQuery';
import type {
  GetGreaseTrapGlobalStatsResponse,
  GetGreaseTrapOptionsResponse,
} from '../../types/greaseTrap';
import { formatNumber, formatNumberMoney } from '../../utils/numberFormatter';

const GreaseTrapGlobalView = () => {
  const { t } = useTranslation('common');
  const [filters, setFilters] = useRecoilState(greaseTrapFiltersState);
  const needRefresh = useRecoilValue(needRefreshState);
  const cleanFilters = useRecoilValue(greaseTrapCleanFiltersState);
  const location = useLocation();
  const { handleOpenGreaseTrapFilterModal, handleOpenNewGreaseTrapModal } =
    useModal();

  const { filterValue, handleFilterChangeWithDebounce } = useDebouceTime({
    setFilters,
    filters,
    key: 'vendorNameFilter',
  });

  const { data: greaseTrapOptions } = useQuery<GetGreaseTrapOptionsResponse>(
    GET_GREASE_TRAP_OPTIONS,
  );

  const { data: globalStats, refetch: refetchGlobalStats } =
    useQuery<GetGreaseTrapGlobalStatsResponse>(GET_GREASE_TRAP_GLOBAL_STATS, {
      variables: {
        vendorId: filters.vendorId || undefined,
        statusFilter: filters.statusFilter || undefined,
        startDate: filters.startDate || undefined,
        endDate: filters.endDate || undefined,
      },
    });

  const {
    fetchMoreGreaseTraps,
    loading,
    greaseTraps,
    hasNextPage,
    errorTimeline,
    loadingSingleQuery,
    refetchGreaseTraps,
    refetchSingleGreaseTrap,
  } = useGreaseTrapList({
    vendorId: filters.vendorId || undefined,
    statusFilter: filters.statusFilter,
    startDate: filters.startDate,
    endDate: filters.endDate,
    limit: filters.limit,
  });

  // Reset filters (with last 30 days dates) when leaving the view
  useEffect(() => {
    setFilters(cleanFilters);
  }, [location.key]);

  useEffect(() => {
    if (needRefresh === 'grease-traps') {
      refetchGreaseTraps();
      refetchGlobalStats();
    } else if (needRefresh === 'grease-traps-stats') {
      refetchGlobalStats();
    }
  }, [needRefresh]);

  const [greaseTrapSentryRef] = useInfiniteScroll({
    loading,
    hasNextPage,
    onLoadMore: fetchMoreGreaseTraps,
    disabled: !!errorTimeline,
    rootMargin: '0px 0px 20px 0px',
  });

  const filteredGreaseTraps = greaseTraps.filter((greaseTrap) => {
    if (!filters.vendorNameFilter) {
      return true;
    }

    return greaseTrap.vendor?.name
      ?.toLowerCase()
      .includes(filters.vendorNameFilter.toLowerCase());
  });

  return (
    <div className="flex w-full flex-col gap-y-2">
      <Breadcrumb title="Grease Traps" image={greaseTrapIcon} />
      <div className="my-2 flex h-full w-full justify-between gap-4">
        <Searchbar
          filterValue={filterValue}
          onChange={handleFilterChangeWithDebounce}
          placeholder="Search by vendor"
          handleOpenFilterModal={handleOpenGreaseTrapFilterModal}
        />
        <div className="relative flex gap-4">
          <AddButton
            icon={addTransactionIcon}
            id="newGreaseTrapButton"
            handleAction={handleOpenNewGreaseTrapModal}
          />
          <RangeDateField
            endDate={filters?.endDate!}
            startDate={filters?.startDate!}
            saveAsString
            onSelectEndChange={(val) =>
              setFilters({
                ...filters,
                endDate: val as string,
              })
            }
            onSelectStartChange={(val) => {
              setFilters({
                ...filters,
                startDate: val as string,
              });
            }}
            updateDates={(start, end) => {
              setFilters({
                ...filters,
                startDate: start as string,
                endDate: end as string,
              });
            }}
            defaultDropdown="last_30"
          />
        </div>
      </div>
      <div className="my-2 flex w-full flex-wrap gap-2">
        {filters.vendorName && (
          <FilterMark
            filterName="vendorName"
            filterValue={filters.vendorName}
            atom={greaseTrapFiltersState}
          />
        )}
        {filters.statusFilter && (
          <FilterMark
            filterName="statusFilter"
            filterValue={filters.statusFilter}
            atom={greaseTrapFiltersState}
          />
        )}
      </div>
      {globalStats && (
        <div className="flex flex-row justify-between gap-x-3">
          <StatCard
            color="text-greenlight"
            stat={`${formatNumber(
              globalStats.getGreaseTrapGlobalStats.total_pounds || 0,
            )} Lbs`}
            subtitle="Total Pounds Received"
            type="small"
          />
          <StatCard
            color="text-greenlight"
            stat={formatNumberMoney(
              globalStats.getGreaseTrapGlobalStats.total_amount || 0,
            )}
            subtitle="Total Amount"
            type="small"
          />
          <StatCard
            color="text-greenlight"
            stat={formatNumberMoney(
              globalStats.getGreaseTrapGlobalStats.pending_balance || 0,
            )}
            subtitle="Pending Balance"
            type="small"
          />
        </div>
      )}
      <div className="w-full">
        <div className="mb-10 flex w-full flex-col gap-y-2">
          {filteredGreaseTraps.length === 0 && !loading && (
            <div className="flex justify-center">
              <p className="text-center text-xs italic">
                {t('vendor-overall.no-timeline-items')}
              </p>
            </div>
          )}
          {loading && <LoaderSmall />}
          {filteredGreaseTraps.map((greaseTrap) => (
            <GreaseTrapCard
              key={greaseTrap.id}
              vendorId={greaseTrap.vendor?.id}
              vendorName={greaseTrap.vendor?.name}
              greaseTrap={greaseTrap}
              loadingSingleQuery={loadingSingleQuery}
              refetchSingleGreaseTrap={refetchSingleGreaseTrap}
              dropdownOptions={{
                driverOptions:
                  greaseTrapOptions?.getGreaseTrapOptions.driverOptions.filter(
                    (option) => option.vendor_id === greaseTrap.vendor_id,
                  ) || [],
                truckOptions:
                  greaseTrapOptions?.getGreaseTrapOptions.truckOptions.filter(
                    (option) => option.vendor_id === greaseTrap.vendor_id,
                  ) || [],
                locationOptions:
                  greaseTrapOptions?.getGreaseTrapOptions.locationOptions || [],
                userOptions:
                  greaseTrapOptions?.getGreaseTrapOptions.userOptions || [],
                vendorOptions:
                  greaseTrapOptions?.getGreaseTrapOptions.vendorOptions || [],
              }}
            />
          ))}
          {(loading || hasNextPage) && (
            <SentryInfiniteScroll ref={greaseTrapSentryRef}>
              <LoaderSmall />
            </SentryInfiniteScroll>
          )}
        </div>
      </div>
    </div>
  );
};

export default GreaseTrapGlobalView;
